import React, { Fragment } from 'react';
import { render } from 'react-dom';
import './index.css';
import './App.Styles.ts';
import 'fontsource-roboto';
import { BrowserRouter as ReactRouter } from 'react-router-dom';
import theme from './App.Styles';
import { Layout } from './components/generic-components/layout/Layout.Component';
import { ThemeProvider } from '@material-ui/core';
import Router from './support/config/Router';
import { ToastContainer } from 'react-toastify';
import {
    AuthenticatedTemplate,
    useMsalAuthentication,
    useMsal,
    UnauthenticatedTemplate
} from '@azure/msal-react';
import 'react-toastify/dist/ReactToastify.css';
import { InteractionType, RedirectRequest } from '@azure/msal-browser';
import { AuthenticationVerifier } from './components/generic-components/authentication-verifier/AuthenticationVerifier.Component';
import { IframeAuthBoundary } from './components/generic-components/iframe-auth-boundary/IframeAuthBoundary.Component';
import { resetPasswordUri, loginUri, scopes, isEmbeddedFromPartner } from './b2cConfiguration';
import { Clarity } from './components/generic-components/clarity/Clarity';

// Evaluated once at module load — stable for the lifetime of the page
const embeddedFromPartner = isEmbeddedFromPartner();

/**
 * Shared app shell rendered after authentication succeeds (either flow).
 */
const AuthenticatedApp: React.FC<{ accounts: any; instance: any }> = ({ accounts, instance }) => (
    <AuthenticationVerifier accounts={accounts} instance={instance}>
        <ReactRouter>
            <ThemeProvider theme={theme}>
                <Layout>
                    <Router />
                    <div id="router-footer" />
                    <ToastContainer autoClose={3000} hideProgressBar />
                </Layout>
            </ThemeProvider>
        </ReactRouter>
    </AuthenticationVerifier>
);

/**
 * Standard direct-access flow — uses redirect-based login (existing behavior).
 */
const DirectAccessApp = () => {
    const loginRequest: RedirectRequest = { scopes: scopes };
    const { result } = useMsalAuthentication(InteractionType.Redirect, loginRequest);
    const { instance, accounts } = useMsal();

    React.useEffect(() => {
        const callbackId = instance.addEventCallback((message) => {
            const { error } = message;
            if (error !== null && error.message.includes('AADB2C90118')) {
                instance.loginRedirect({
                    authority: resetPasswordUri,
                    scopes: scopes
                });
                return;
            }
            if (error !== null && error.message.includes('AADB2C90091')) {
                instance.loginRedirect({
                    authority: loginUri,
                    scopes: scopes
                });
                return;
            }
        });

        // Detect environment changes (Dev <-> Test <-> Prod) and close session
        const currentHost = window.location.hostname;
        const lastHost = localStorage.getItem('lastHost');

        if (lastHost && lastHost !== currentHost) {
            instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
        }

        localStorage.setItem('lastHost', currentHost);

        return () => callbackId && instance.removeEventCallback(callbackId);
    }, [instance]);

    return (
        <Fragment>
            <AuthenticatedTemplate>
                {accounts ? <AuthenticatedApp accounts={accounts} instance={instance} /> : ''}
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate />
        </Fragment>
    );
};

/**
 * Iframe flow — uses ssoSilent with domain_hint (no redirects).
 */
const IframeApp = () => {
    const { instance, accounts } = useMsal();

    return (
        <IframeAuthBoundary domainHint="commonaad">
            {accounts ? <AuthenticatedApp accounts={accounts} instance={instance} /> : ''}
        </IframeAuthBoundary>
    );
};

/**
 * Root component — picks the auth strategy based on iframe detection.
 */
const App = () => {
    const clarityEnabled = String(process.env.REACT_APP_CLARITY_ENABLED);
    const clarityProjectId = String(process.env.REACT_APP_CLARITY_PROJECT_ID);

    return (
        <>
            {clarityEnabled === 'true' && clarityProjectId && (
                <Clarity clarityId={clarityProjectId} />
            )}
            {embeddedFromPartner ? <IframeApp /> : <DirectAccessApp />}
        </>
    );
};

render(<App />, document.getElementById('root'));

export default App;
